/**
 * Staff navigation model (direction B, modes).
 *
 * One definition per app (mode -> pages -> path, label, icon, access) drives the
 * top bar, sub-menus, search, breadcrumbs and the phone mode switcher. These are
 * pure functions: the active mode is always derived from the URL, never stored.
 */
import type { ComponentType } from "react";

export type NavIcon = ComponentType<{
  fontSize?: "inherit" | "small" | "medium";
}>;

export interface NavPage<A> {
  /** Stable id, e.g. "front-desk". */
  id: string;
  /** Final name, shared by mode sub-menu, breadcrumb, search and phone switcher. */
  label: string;
  /** Href; may carry a required query, e.g. "/services/drop-in?tab=dashboard". */
  path: string;
  icon: NavIcon;
  /** Extra paths/prefixes that select this page (same query rule as `path`). */
  matchPaths?: readonly string[];
  /** App-specific requirement, evaluated by the app's access evaluator. */
  access?: A;
  /** Extra search terms. */
  keywords?: readonly string[];
  /** Rendered in the user menu instead of the mode sub-menu. */
  inUserMenu?: boolean;
  /**
   * Routes that make up this page, shown as a tab row while the page is
   * active. A page with sections is visible only when one of them is, and
   * links to its first visible section when its own `path` is not visible.
   */
  sections?: readonly NavSection<A>[];
}

export interface NavSection<A> {
  id: string;
  label: string;
  path: string;
  matchPaths?: readonly string[];
  /** Defaults to the page's `access`. */
  access?: A;
  keywords?: readonly string[];
}

export interface NavMode<A> {
  id: string;
  label: string;
  icon: NavIcon;
  pages: readonly NavPage<A>[];
  /** Render the mode tab as its icon only (label stays the accessible name). */
  iconOnly?: boolean;
}

export interface NavDefinition<A> {
  app: "business" | "platform";
  homePageId: string;
  modes: readonly NavMode<A>[];
}

export type AccessEvaluator<A> = (access: A | undefined) => boolean;

export interface ActiveLocation<A> {
  mode: NavMode<A>;
  page: NavPage<A>;
  /** The page's best-matching section, when it has sections. */
  section: NavSection<A> | null;
}

export interface NavSearchEntry {
  pageId: string;
  label: string;
  modeLabel: string;
  /** For a section: the page it belongs to. */
  parentLabel?: string;
  path: string;
  keywords: readonly string[];
  /** The page's icon (a section shows its page's). */
  icon: NavIcon;
}

function visiblePage<A>(page: NavPage<A>, allow: AccessEvaluator<A>): NavPage<A> | null {
  if (!allow(page.access)) return null;
  if (!page.sections) return page;
  // A section without its own requirement inherits its page's.
  const sections = page.sections.filter((section) =>
    allow(section.access ?? page.access),
  );
  if (sections.length === 0) return null;
  const path = sections.some((section) => section.path === page.path)
    ? page.path
    : sections[0]!.path;
  return { ...page, path, sections };
}

/**
 * Modes in definition order with only the pages (and sections) `allow`
 * accepts; pages without a visible section and empty modes are dropped.
 */
export function visibleModes<A>(
  def: NavDefinition<A>,
  allow: AccessEvaluator<A>,
): NavMode<A>[] {
  const modes: NavMode<A>[] = [];
  for (const mode of def.modes) {
    const pages = mode.pages
      .map((page) => visiblePage(page, allow))
      .filter((page): page is NavPage<A> => page !== null);
    if (pages.length > 0) modes.push({ ...mode, pages });
  }
  return modes;
}

/**
 * The pathname as React Router matches it: each segment percent-decoded (an
 * encoded "/" stays encoded) and the whole path lowercased, since routes match
 * case-insensitively. Malformed escapes keep their raw text.
 */
export function normalizePathname(pathname: string): string {
  return pathname
    .split("/")
    .map((segment) => {
      try {
        return decodeURIComponent(segment).replace(/\//g, "%2F");
      } catch {
        return segment;
      }
    })
    .join("/")
    .toLowerCase();
}

interface Candidate {
  pathname: string;
  params: [string, string][];
}

function parseCandidate(href: string): Candidate {
  const queryIndex = href.indexOf("?");
  const pathname = queryIndex === -1 ? href : href.slice(0, queryIndex);
  const query = queryIndex === -1 ? "" : href.slice(queryIndex + 1);
  return { pathname, params: [...new URLSearchParams(query).entries()] };
}

function pathMatches(candidate: string, pathname: string): boolean {
  if (pathname === candidate) return true;
  const prefix = candidate.endsWith("/") ? candidate : `${candidate}/`;
  // A root candidate ("/") selects only the root itself.
  if (prefix === "/") return false;
  return pathname.startsWith(prefix);
}

function candidateMatches(
  candidate: Candidate,
  pathname: string,
  search: URLSearchParams,
): boolean {
  if (!pathMatches(candidate.pathname, pathname)) return false;
  // First-value semantics, like URLSearchParams#get in the routed pages.
  return candidate.params.every(([key, value]) => search.get(key) === value);
}

interface Score {
  length: number;
  queryCount: number;
}

/** Best (longest pathname, then most query params) matching href, or null. */
function bestScore(
  hrefs: readonly string[],
  pathname: string,
  params: URLSearchParams,
): Score | null {
  let best: Score | null = null;
  for (const href of hrefs) {
    const candidate = parseCandidate(href);
    if (!candidateMatches(candidate, pathname, params)) continue;
    const score = { length: candidate.pathname.length, queryCount: candidate.params.length };
    if (best === null || outranks(score, best)) best = score;
  }
  return best;
}

function outranks(score: Score, other: Score): boolean {
  return (
    score.length > other.length ||
    (score.length === other.length && score.queryCount > other.queryCount)
  );
}

function sectionHrefs<A>(section: NavSection<A>): string[] {
  return [section.path, ...(section.matchPaths ?? [])];
}

/**
 * The mode, page and section selected by a URL. Among matching candidates the
 * longest pathname wins, then the one with more required query params, then
 * the earlier page. Returns null when nothing matches (deep link to a page the
 * user may not see, or a URL-only page).
 */
export function resolveActive<A>(
  modes: readonly NavMode<A>[],
  pathname: string,
  search: string,
): ActiveLocation<A> | null {
  // Match what the router renders: /ANALYTICS/revenue and /%70rograms are the
  // same pages as /analytics/revenue and /programs.
  pathname = normalizePathname(pathname);
  const params = new URLSearchParams(search);
  let best: { mode: NavMode<A>; page: NavPage<A>; score: Score } | null = null;
  for (const mode of modes) {
    for (const page of mode.pages) {
      const hrefs = [
        page.path,
        ...(page.matchPaths ?? []),
        ...(page.sections ?? []).flatMap(sectionHrefs),
      ];
      const score = bestScore(hrefs, pathname, params);
      if (score && (best === null || outranks(score, best.score))) {
        best = { mode, page, score };
      }
    }
  }
  if (!best) return null;
  let section: NavSection<A> | null = null;
  let sectionScore: Score | null = null;
  for (const candidate of best.page.sections ?? []) {
    const score = bestScore(sectionHrefs(candidate), pathname, params);
    if (score && (sectionScore === null || outranks(score, sectionScore))) {
      section = candidate;
      sectionScore = score;
    }
  }
  return { mode: best.mode, page: best.page, section };
}

export type RouteAccess =
  | { kind: "allow" }
  | { kind: "deny" }
  /** The page is visible but not this section: go to its first visible one. */
  | { kind: "redirect"; to: string };

/**
 * Whether a URL the menu knows about is open to this user, using the same
 * rules as the menu. A URL that no page or section claims is allowed (the
 * route keeps its own guard). A page the user cannot see is denied; a hidden
 * section of a visible page redirects to the page's first visible section.
 */
export function routeAccess<A>(
  def: NavDefinition<A>,
  allow: AccessEvaluator<A>,
  pathname: string,
  search: string,
): RouteAccess {
  const target = resolveActive(def.modes, pathname, search);
  if (!target) return { kind: "allow" };
  const page = visibleModes(def, allow)
    .find((mode) => mode.id === target.mode.id)
    ?.pages.find((candidate) => candidate.id === target.page.id);
  if (!page) return { kind: "deny" };
  if (!target.section) return { kind: "allow" };
  if (page.sections?.some((section) => section.id === target.section?.id)) {
    return { kind: "allow" };
  }
  return { kind: "redirect", to: page.path };
}

export function breadcrumb<A>(
  active: ActiveLocation<A> | null,
  showMode: boolean,
): string[] {
  if (!active) return [];
  return showMode ? [active.mode.label, active.page.label] : [active.page.label];
}

/**
 * Every visible page once, under its mode, each followed by its sections that
 * are not the page's own link target (labelled with their page). The page's
 * entry also matches its target section's label and keywords.
 */
export function searchEntries<A>(modes: readonly NavMode<A>[]): NavSearchEntry[] {
  const seen = new Set<string>();
  const entries: NavSearchEntry[] = [];
  for (const mode of modes) {
    for (const page of mode.pages) {
      if (seen.has(page.id)) continue;
      seen.add(page.id);
      // The section the page links to has no entry of its own, so the page's
      // entry answers to that section's name and terms too.
      const target = page.sections?.find((section) => section.path === page.path);
      entries.push({
        pageId: page.id,
        label: page.label,
        modeLabel: mode.label,
        path: page.path,
        icon: page.icon,
        keywords: target
          ? [...(page.keywords ?? []), target.label, ...(target.keywords ?? [])]
          : (page.keywords ?? []),
      });
      for (const section of page.sections ?? []) {
        if (section.path === page.path) continue;
        entries.push({
          pageId: `${page.id}:${section.id}`,
          label: section.label,
          modeLabel: mode.label,
          parentLabel: page.label,
          path: section.path,
          icon: page.icon,
          keywords: section.keywords ?? [],
        });
      }
    }
  }
  return entries;
}

/**
 * Home if visible; else the first visible menu page; else My coaching (a user
 * whose only page is their own coaching portal); else the first visible page;
 * else null. User-menu pages never outrank a menu page: My coaching is open to
 * every signed-in user, so preferring it would land front desk staff there.
 */
export function landingPath<A>(
  def: NavDefinition<A>,
  modes: readonly NavMode<A>[],
): string | null {
  const pages = modes.flatMap((mode) => mode.pages);
  const home = pages.find((page) => page.id === def.homePageId);
  if (home) return home.path;
  const firstMenuPage = pages.find((page) => !page.inUserMenu);
  if (firstMenuPage) return firstMenuPage.path;
  const coaching = pages.find((page) => page.id === "my-coaching");
  if (coaching) return coaching.path;
  return pages[0]?.path ?? null;
}

function storageKey(key: string, modeId: string): string {
  return `bw:nav:${key}:${modeId}`;
}

export function rememberPath(
  storage: Pick<Storage, "setItem"> | null,
  key: string,
  modeId: string,
  path: string,
): void {
  if (!storage) return;
  try {
    storage.setItem(storageKey(key, modeId), path);
  } catch {
    // Private mode or quota: memory is a convenience, never a requirement.
  }
}

/**
 * The mode's remembered page or section, or its first page when nothing valid
 * is stored. Only a visible page or section link of this mode is accepted, so
 * a stored detail URL (or a page the user lost) is never replayed.
 */
export function recallPath<A>(
  storage: Pick<Storage, "getItem"> | null,
  key: string,
  mode: NavMode<A>,
): string {
  const fallback = mode.pages[0]?.path ?? "/";
  if (!storage) return fallback;
  let stored: string | null = null;
  try {
    stored = storage.getItem(storageKey(key, mode.id));
  } catch {
    return fallback;
  }
  if (!stored) return fallback;
  const links = mode.pages.flatMap((page) => [
    page.path,
    ...(page.sections ?? []).map((section) => section.path),
  ]);
  return links.includes(stored) ? stored : fallback;
}
